import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSetupWizard } from "../../context/SetupWizardContext";
import { SETUP_STEPS } from "./SetupWizardProvider";
import {
  BASE,
  BORDER,
  BRAND,
  GREY,
  PRIMARY,
  SUCCESS,
  ERROR,
} from "../../utils/constants/colors";
import { fontSize, fontWeight } from "../../utils/constants/UI";

// ── Design tokens (matching TourTooltip exactly) ──────────────────────────────
const CARD_SHADOW = "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)";
const OVERLAY_BG = "rgba(30, 41, 57, 0.72)";
const CARD_RADIUS = 12;
const CARD_WIDTH = 360;
const STEP_CARD_WIDTH = 520;
const BACKDROP_FILTER = "blur(4px)";

// ── Keyframes injected once ───────────────────────────────────────────────────
const KEYFRAMES = `
  @keyframes sw-fade-in {
    from { opacity: 0; transform: scale(0.96); }
    to   { opacity: 1; transform: scale(1); }
  }
  @keyframes sw-mini-in {
    from { opacity: 0; transform: translateX(-8px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes sw-minimize-shake {
    0%, 100% { transform: translateX(0) rotate(0deg); }
    15%  { transform: translateX(-5px) rotate(-3deg); }
    30%  { transform: translateX(5px) rotate(3deg); }
    45%  { transform: translateX(-4px) rotate(-2deg); }
    60%  { transform: translateX(4px) rotate(2deg); }
    75%  { transform: translateX(-2px) rotate(-1deg); }
    90%  { transform: translateX(2px) rotate(1deg); }
  }
  @keyframes sw-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes sw-mini-alert {
    0%   { transform: translateX(0) rotate(0deg); }
    3%   { transform: translateX(-3px) rotate(-1.5deg); }
    6%   { transform: translateX(3px) rotate(1.5deg); }
    9%   { transform: translateX(-2px) rotate(-1deg); }
    12%  { transform: translateX(2px) rotate(1deg); }
    15%  { transform: translateX(-1px) rotate(-0.5deg); }
    18%  { transform: translateX(1px) rotate(0.5deg); }
    21%  { transform: translateX(0) rotate(0deg); }
    100% { transform: translateX(0) rotate(0deg); }
  }
`;
let _injected = false;
function injectKeyframes() {
  if (_injected) return;
  _injected = true;
  const el = document.createElement("style");
  el.setAttribute("data-setup-wizard", "true");
  el.textContent = KEYFRAMES;
  document.head.appendChild(el);
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function SegmentedProgress({ keys, currentKey, doneKeys }) {
  const total = keys.length;
  const currentIdx = keys.indexOf(currentKey);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
      <div style={{ flex: 1, display: "flex", gap: 3 }}>
        {keys.map((k, i) => {
          const isDone = doneKeys.includes(k);
          const isCurrent = i === currentIdx;
          return (
            <div
              key={k}
              style={{
                flex: isCurrent ? 1.6 : 1,
                height: isCurrent ? 5 : 4,
                borderRadius: 3,
                background: isDone
                  ? SUCCESS.W_500
                  : isCurrent
                  ? BRAND.PRIMARY
                  : BORDER.DEFAULT,
                transition: "all 0.3s ease",
              }}
            />
          );
        })}
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: GREY.TERTIARY, minWidth: 28, textAlign: "right" }}>
        {total > 0 ? Math.round(((doneKeys.filter(k => keys.includes(k)).length + (currentIdx >= 0 ? 0 : 0)) / total) * 100) : 0}%
      </span>
    </div>
  );
}

function CardHeader({ label, stepText, onMinimize, minimizeShakeKey }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: BRAND.PRIMARY, display: "inline-block", flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: fontWeight.SEMIBOLD, color: GREY.PRIMARY, textTransform: "uppercase", letterSpacing: 0.7 }}>
          {label}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {stepText && (
          <span style={{ background: PRIMARY.W_100, color: BRAND.PRIMARY, border: `1px solid ${PRIMARY.W_200}`, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: fontWeight.SEMIBOLD }}>
            {stepText}
          </span>
        )}
        {onMinimize && (
          <button
            key={minimizeShakeKey}
            onClick={onMinimize}
            title="Hide"
            style={{
              background: "none",
              border: `1.5px solid ${BORDER.DARK}`,
              borderRadius: 6,
              cursor: "pointer",
              padding: "3px 10px",
              fontSize: 12,
              fontWeight: fontWeight.BOLD,
              color: GREY.PRIMARY,
              fontFamily: "inherit",
              lineHeight: "18px",
              display: "flex",
              alignItems: "center",
              gap: 4,
              animation: minimizeShakeKey > 0 ? "sw-minimize-shake 0.55s ease" : "none",
            }}
          >
            Hide
          </button>
        )}
      </div>
    </div>
  );
}

function Divider({ mb = 14, mt = 0 }) {
  return <div style={{ height: 2, background: BORDER.DARK, marginBottom: mb, marginTop: mt }} />;
}

function PrimaryBtn({ onClick, children, loading, style }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        background: loading ? GREY.TERTIARY : BRAND.PRIMARY,
        border: "none",
        borderRadius: 8,
        color: BASE.WHITE,
        cursor: loading ? "not-allowed" : "pointer",
        padding: "6px 18px",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "inherit",
        transition: "background 0.15s ease",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function GhostBtn({ onClick, children, style }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: BASE.WHITE,
        border: `1px solid ${BORDER.DARK}`,
        borderRadius: 8,
        color: GREY.PRIMARY,
        cursor: "pointer",
        padding: "6px 14px",
        fontSize: 12,
        fontWeight: 500,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function LinkBtn({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{ background: "none", border: "none", color: GREY.SECONDARY, fontSize: 12, cursor: "pointer", padding: 0, fontFamily: "inherit" }}
    >
      {children}
    </button>
  );
}

function DetectingBtn() {
  injectKeyframes();
  return (
    <button
      disabled
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        background: PRIMARY.W_50,
        border: `1.5px solid ${BRAND.PRIMARY}`,
        borderRadius: 8,
        color: BRAND.PRIMARY,
        cursor: "not-allowed",
        padding: "6px 14px",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 11,
          height: 11,
          border: `2px solid ${BRAND.PRIMARY}`,
          borderTopColor: "transparent",
          borderRadius: "50%",
          display: "inline-block",
          flexShrink: 0,
          animation: "sw-spin 0.8s linear infinite",
        }}
      />
      Auto detecting completion...
    </button>
  );
}

function ContinuingBtn() {
  injectKeyframes();
  return (
    <button
      disabled
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        background: BRAND.PRIMARY,
        border: "none",
        borderRadius: 8,
        color: BASE.WHITE,
        cursor: "not-allowed",
        padding: "6px 18px",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        opacity: 0.9,
      }}
    >
      <span
        style={{
          width: 11,
          height: 11,
          border: `2px solid ${BASE.WHITE}`,
          borderTopColor: "transparent",
          borderRadius: "50%",
          display: "inline-block",
          flexShrink: 0,
          animation: "sw-spin 0.8s linear infinite",
        }}
      />
      Continuing...
    </button>
  );
}

function KBContinueBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        background: BRAND.PRIMARY,
        border: "none",
        borderRadius: 8,
        color: BASE.WHITE,
        cursor: "pointer",
        padding: "6px 18px",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        transition: "background 0.15s ease",
      }}
    >
      Continue
    </button>
  );
}

// Backdrop + centered card wrapper (for intro/outro)
function ModalOverlay({ children }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: OVERLAY_BG,
        backdropFilter: BACKDROP_FILTER,
      }}
    >
      {children}
    </div>
  );
}

// Centered card wrapper (no backdrop — for step, so user can interact with page)
// Split into two divs: outer handles fixed-center positioning, inner handles the
// scale animation. Keeping them separate prevents the animation's transform from
// overriding translate(-50%, -50%) on the same element (which caused the card to
// jump to the top-left corner at animation start before snapping back to center).
function FloatingCard({ children, style }) {
  injectKeyframes();
  return (
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 9200,
        fontFamily: "inherit",
      }}
    >
      <div
        style={{
          width: STEP_CARD_WIDTH,
          background: BASE.WHITE,
          borderRadius: CARD_RADIUS,
          boxShadow: CARD_SHADOW,
          padding: "20px 28px 20px",
          animation: "sw-fade-in 0.22s ease",
          ...style,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ── Status row for intro/outro ────────────────────────────────────────────────
function SetupStatusRow({ stepKey, stepsStatus, skippedSteps, pending }) {
  const step = SETUP_STEPS.find((s) => s.key === stepKey);
  const isDone = stepsStatus[stepKey];
  const isSkipped = skippedSteps.includes(stepKey);
  const isPending = pending;

  let icon, color;
  if (isDone) { icon = "✓"; color = SUCCESS.W_500; }
  else if (isSkipped) { icon = "⊘"; color = GREY.TERTIARY; }
  else if (isPending) { icon = "○"; color = GREY.TERTIARY; }
  else { icon = "✗"; color = ERROR.W_500; }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", background: BASE.BACKGROUND_LIGHT, borderRadius: 8, marginBottom: 6 }}>
      <span style={{ fontSize: fontSize.SM, fontWeight: fontWeight.MEDIUM, color: isDone ? GREY.PRIMARY : GREY.SECONDARY }}>{step?.label}</span>
      {isDone && <span style={{ marginLeft: "auto", fontSize: 11, color: SUCCESS.W_500, fontWeight: 600 }}>Done</span>}
      {isSkipped && <span style={{ marginLeft: "auto", fontSize: 11, color: GREY.TERTIARY }}>Skipped</span>}
      {isPending && <span style={{ marginLeft: "auto", fontSize: 11, color: GREY.TERTIARY }}>Pending</span>}
    </div>
  );
}

// ── Intro modal ───────────────────────────────────────────────────────────────
function IntroModal() {
  const { proceedFromIntro } = useSetupWizard();

  return (
    <ModalOverlay>
      <div
        style={{
          width: CARD_WIDTH,
          background: BASE.WHITE,
          borderRadius: CARD_RADIUS,
          boxShadow: CARD_SHADOW,
          padding: "20px 24px 18px",
          fontFamily: "inherit",
          animation: "sw-fade-in 0.25s ease",
        }}
      >
        <CardHeader label="ReachIQ Setup" />
        <Divider mb={14} />

        <h4 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700, color: GREY.PRIMARY, lineHeight: 1.3 }}>
          Let's Get You Set Up First
        </h4>
        <p style={{ margin: "0 0 20px", fontSize: fontSize.SM, fontWeight: fontWeight.MEDIUM, color: GREY.SECONDARY, lineHeight: 1.65 }}>
          Before diving into the product tour, let's make sure a few essentials are in place. It only takes a few minutes, and you will be all set to start reaching out right away!
        </p>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          <PrimaryBtn onClick={proceedFromIntro}>Let's start</PrimaryBtn>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ── Step card (expanded) ──────────────────────────────────────────────────────
function StepCard() {
  const {
    currentStepKey,
    initialPendingKeys,
    verifying,
    verifyError,
    confirmStepDone,
    skipCurrentStep,
    minimizeWizard,
    kbDetectionState,
    icpDetectionState,
    mailboxDetectionState,
    signatureDetectionState,
  } = useSetupWizard();

  const [shakeKey, setShakeKey] = useState(0);

  useEffect(() => {
    setShakeKey(k => k + 1);
    const id = setInterval(() => setShakeKey(k => k + 1), 15000);
    return () => clearInterval(id);
  }, []);

  const step = SETUP_STEPS.find((s) => s.key === currentStepKey);
  if (!step) return null;

  const stepIdx = initialPendingKeys.indexOf(currentStepKey);
  const total = initialPendingKeys.length;

  const isKBStep = currentStepKey === "kb";
  const isKBReady = isKBStep && kbDetectionState === "ready";
  const isKBContinuing = isKBStep && kbDetectionState === "continuing";

  const isICPStep = currentStepKey === "icp";
  const isICPContinuing = isICPStep && icpDetectionState === "continuing";

  const isMailboxStep = currentStepKey === "mailbox";
  const isMailboxContinuing = isMailboxStep && mailboxDetectionState === "continuing";

  const isSignatureStep = currentStepKey === "signature";
  const isSignatureContinuing = isSignatureStep && signatureDetectionState === "continuing";

  const isContinuing = isKBContinuing || isICPContinuing || isMailboxContinuing || isSignatureContinuing;
  const showVerifyFallback = verifyError && !isKBReady && !isContinuing;

  return (
    <FloatingCard>
      <CardHeader
        label="ReachIQ Setup"
        stepText={total > 0 ? `Step ${stepIdx + 1} of ${total}` : undefined}
        onMinimize={minimizeWizard}
        minimizeShakeKey={shakeKey}
      />

      <Divider />

      {/* Title + description */}
      <div style={{ paddingLeft: 2, paddingRight: 2, marginBottom: 16 }}>
        <h4 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: fontWeight.BOLD, color: GREY.PRIMARY, lineHeight: 1.35 }}>
          {step.title}
        </h4>
        <p style={{ margin: 0, fontSize: fontSize.SM, fontWeight: fontWeight.MEDIUM, lineHeight: 1.7, color: GREY.QUATINARY }}>
          {step.description}
        </p>
      </div>

      {/* Error hint — not shown when KB is in ready state */}
      {showVerifyFallback && (
        <div style={{ padding: "10px 14px", background: PRIMARY.W_50, border: `1px solid ${PRIMARY.W_200}`, borderRadius: 8, marginBottom: 14 }}>
          <p style={{ margin: 0, fontSize: fontSize.XS, color: ERROR.W_500, lineHeight: 1.5, fontWeight: fontWeight.MEDIUM }}>
            {verifyError === "notDone" ? step.notDoneHint : "Something went wrong. Please try again."}
          </p>
        </div>
      )}

      <Divider mb={14} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <LinkBtn onClick={skipCurrentStep}>Skip step</LinkBtn>
        {isContinuing ? (
          <ContinuingBtn />
        ) : isKBReady ? (
          <KBContinueBtn onClick={confirmStepDone} />
        ) : verifyError ? (
          <PrimaryBtn onClick={confirmStepDone} loading={verifying}>
            {verifyError === "notDone" ? "Please try again" : "Retry"}
          </PrimaryBtn>
        ) : verifying ? (
          <PrimaryBtn loading={verifying}>Checking...</PrimaryBtn>
        ) : (
          <DetectingBtn />
        )}
      </div>
    </FloatingCard>
  );
}

// ── Step card (minimized) ─────────────────────────────────────────────────────
function MiniCard() {
  const { currentStepKey, kbDetectionState, maximizeWizard } = useSetupWizard();
  const step = SETUP_STEPS.find((s) => s.key === currentStepKey);
  const label = step ? step.title : "Setup";
  const truncated = label.length > 20 ? label.slice(0, 20) + "..." : label;
  const isKbAlert = currentStepKey === "kb" && kbDetectionState === "ready";

  injectKeyframes();
  return (
    <div
      style={{
        position: "fixed",
        top: 80,
        left: 20,
        zIndex: 9200,
        background: BASE.WHITE,
        borderRadius: 10,
        boxShadow: CARD_SHADOW,
        padding: "8px 14px",
        fontFamily: "inherit",
        display: "flex",
        alignItems: "center",
        gap: 10,
        cursor: "pointer",
        animation: isKbAlert
          ? "sw-mini-in 0.18s ease, sw-mini-alert 10s ease 0.2s infinite"
          : "sw-mini-in 0.18s ease",
        border: `1px solid ${BORDER.DEFAULT}`,
        maxWidth: 240,
        transition: "box-shadow 0.15s ease, border-color 0.2s ease",
      }}
      onClick={maximizeWizard}
      title="Click to expand"
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: BRAND.PRIMARY, flexShrink: 0, display: "inline-block" }} />
      <div style={{ minWidth: 0 }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: GREY.TERTIARY, textTransform: "uppercase", letterSpacing: 0.6, display: "block" }}>
          Setup
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, color: GREY.PRIMARY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
          {truncated}
        </span>
      </div>
      <span style={{ marginLeft: "auto", fontSize: 14, color: GREY.TERTIARY, flexShrink: 0 }}>+</span>
    </div>
  );
}

// ── Outro modal ───────────────────────────────────────────────────────────────
function OutroModal() {
  const [outroLoading, setOutroLoading] = useState(false);
  const {
    stepsStatus, skippedSteps, initialPendingKeys, launchSource,
    proceedFromOutro, dismissWizard,
  } = useSetupWizard();

  const allDone = initialPendingKeys.every((k) => stepsStatus[k]);
  const anySkipped = skippedSteps.length > 0;

  // Show all 4 steps in the outro summary
  const allKeys = ["mailbox", "signature", "icp", "kb"];

  const handleProceed = async () => {
    if (launchSource === "welcome") {
      setOutroLoading(true);
      try {
        await proceedFromOutro();
      } finally {
        setOutroLoading(false);
      }
    } else {
      dismissWizard();
    }
  };

  return (
    <ModalOverlay>
      <div
        style={{
          width: CARD_WIDTH,
          background: BASE.WHITE,
          borderRadius: CARD_RADIUS,
          boxShadow: CARD_SHADOW,
          padding: "20px 24px 18px",
          fontFamily: "inherit",
          animation: "sw-fade-in 0.25s ease",
        }}
      >
        <CardHeader label="ReachIQ Setup" />
        <Divider mb={14} />

        <h4 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700, color: GREY.PRIMARY, lineHeight: 1.3 }}>
          {allDone && !anySkipped ? "You're All Set!" : "Almost There"}
        </h4>
        <p style={{ margin: "0 0 16px", fontSize: fontSize.SM, fontWeight: fontWeight.MEDIUM, color: GREY.SECONDARY, lineHeight: 1.65 }}>
          {allDone && !anySkipped
            ? "Everything is in place. Let's now show you around ReachIQ so you are fully ready to start reaching out."
            : <>A few things are still pending. You can complete them anytime from <span style={{ fontWeight: fontWeight.SEMIBOLD, color: GREY.PRIMARY }}>Settings → Learning Center</span>. For now, let's head to the product tour.</>}
        </p>

        <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 600, color: GREY.TERTIARY, textTransform: "uppercase", letterSpacing: "0.07em" }}>
          Setup summary
        </p>
        {allKeys.map((key) => (
          <SetupStatusRow
            key={key}
            stepKey={key}
            stepsStatus={stepsStatus}
            skippedSteps={skippedSteps}
            pending={!stepsStatus[key] && !skippedSteps.includes(key) && !initialPendingKeys.includes(key)}
          />
        ))}

        <Divider mt={16} mb={14} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {anySkipped && !outroLoading && (
            <GhostBtn onClick={dismissWizard}>Maybe later</GhostBtn>
          )}
          <PrimaryBtn
            style={{ marginLeft: "auto" }}
            onClick={handleProceed}
            loading={outroLoading}
          >
            {launchSource === "welcome"
              ? outroLoading ? "Starting Tour..." : "Start the Tour"
              : "Done"}
          </PrimaryBtn>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ── Root component ────────────────────────────────────────────────────────────
export default function SetupWizard() {
  const { wizardPhase, minimized } = useSetupWizard();

  if (wizardPhase === "idle" || wizardPhase === "checking") return null;

  let content = null;
  if (wizardPhase === "intro") {
    content = <IntroModal />;
  } else if (wizardPhase === "step") {
    content = minimized ? <MiniCard /> : <StepCard />;
  } else if (wizardPhase === "outro") {
    content = <OutroModal />;
  }

  if (!content) return null;
  return createPortal(content, document.body);
}
